import { useContext } from 'react'

import { Helmet } from 'react-helmet-async'

import { faFileInvoiceDollar } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import useAgencyProviders from '@hooks/useAgencyProviders'
import useAgencySelector from '@hooks/useAgencySelector'
import { generateUrls } from '@hooks/useProviderPriceList'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext from '@contexts/pageContext'

import { AgencyProviderModel } from '@models/agencyProvider'

import PriceListItem from '../PriceListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'price_list_string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: { pageFilters: PageContentProps }) => {
  const { pageFilters } = props

  const { selectedAgencyId } = useAgencySelector()

  const useProviderPriceListsPayload = useAgencyProviders({
    filters: {
      ...pageFilters,
      agency_id: selectedAgencyId,
    },
    requestOptions: {
      include_agency_provider_provider_price_list: true,
      include_agency_provider_provider: true,
      include_agency_provider_service: true,
    },
    performHttpRequests: !!selectedAgencyId,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviders,
    filteredAgencyProvidersCount,
    hasFilteredAgencyProviders,
    loading,
  } = useProviderPriceListsPayload

  const { providerPriceListsIndexUrl } = generateUrls({}, {
    prefixRoute: `agencies/${selectedAgencyId}/manage`,
  })

  const pageContext = useContext(PageContext)
  const {
    callbacks: { resetFilters, updateFilters },
    defaultFilters,
  } = pageContext

  return (
    <>
      <Helmet>
        <title>Price Lists | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="price_list"
        nestedNavigation
        tabBarItems={[
          {
            href: providerPriceListsIndexUrl,
            icon: faFileInvoiceDollar,
            key: 'price_list',
            title: 'price lists',
          },
        ]}
        title="Price Lists"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.priceLists.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAgencyProviders && <ListBlankState />}

            {!loading && hasFilteredAgencyProviders && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Price Lists">
                {/*
                 // @ts-expect-error TS2322 */}
                <DashboardModule.ScrollableSection flexDirection="column" maxHeight={600}>
                  {filteredAgencyProviders.map((agencyProvider: AgencyProviderModel) => (
                    <PriceListItem
                      key={agencyProvider.id}
                      agencyProvider={agencyProvider}
                    />
                  ))}
                </DashboardModule.ScrollableSection>

                <DashboardModule.LoadMoreFooter
                  boxProps={{ borderTop: '1px solid' }}
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredAgencyProvidersCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.priceLists.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
